import { useCallback } from "react";

import { useTranslation } from "next-i18next";

export type CopyEntry = { nl: string; en: string };

/** Dutch when the site runs in Dutch (or the language is unknown), English otherwise. */
export const isDutchUi = (language: string | undefined): boolean =>
  (language ?? "nl").toLowerCase().startsWith("nl");

/**
 * Builds the copy hook for one source table (sales, payment settings, ...).
 *
 * `copy(key, values)` renders the entry through Lokalise; until the key
 * exists there, the source string in the UI's own language shows: English on
 * the English site, Dutch on the Dutch one. `defaults(key)` is that string
 * alone and `key(copyKey)` the Lokalise key, for `Trans`, which takes them
 * separately. The default travels as t()'s positional second argument, the
 * form every spec's translation mock understands.
 */
export const createCopyHook =
  <T extends Record<string, CopyEntry>>(
    table: T,
    lokaliseKey: (copyKey: keyof T & string) => string
  ) =>
  () => {
    const { t, i18n } = useTranslation();
    const dutch = isDutchUi(i18n?.language);

    const defaults = useCallback(
      (copyKey: keyof T & string) => {
        const entry = table[copyKey] as CopyEntry;

        return dutch ? entry.nl : entry.en;
      },
      [dutch]
    );

    const copy = useCallback(
      (copyKey: keyof T & string, values?: Record<string, string | number>) =>
        t(lokaliseKey(copyKey), defaults(copyKey), values),
      [defaults, t]
    );

    return { copy, key: lokaliseKey, defaults };
  };
